import { c as _c } from "react/compiler-runtime";
import { useField } from 'formik';
import { FocusEvent, KeyboardEvent, useState } from 'react';
import styled, { css } from 'styled-components';
import useSWR from 'swr';
import { expandResourcePatternShorthand, getResourcePatternError, matchesResourcePattern } from '../../../../../../../../../domain/issue-trigger-patterns';
import { APIList } from '../../../../../../../../../typings/API.interface';
import field_formats from '../../../../../../utils/field-formatters';
import Icon from '../../../../../common/base/Icon';
import Text from '../../../../../common/base/Text';
import TextInput from '../../../../../common/Form/Fields/TextInput';
import { Div } from '../../../../../common/helpers/StyledUtils';
export const RESOURCE_PATTERN_PREVIEW_LIMIT = 100;
interface Props<T> {
  name: string;
  label: string;
  swr_key: string;
  fetcher: () => Promise<APIList<T>>;
  getName: (model: T) => string;
}

// Mirrors the JSONFieldSelectorInput dropdown so field pickers look alike
const StyledDropdown = styled(Div)(({
  theme
}) => css`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${theme.colors.surface.base.surface};
    border-radius: ${theme.radius.normal};
    box-shadow: ${theme.elevation[2]};
    z-index: ${theme.zindex.dropdown};
    max-height: 220px;
    overflow-y: auto;
    /* Clears the input's 4px focus ring with a visible 4px gap */
    margin-top: ${theme.spacing(2)};
    padding: ${theme.spacing(1)};
  `);
const StyledMatchRow = styled(Div)(({
  theme
}) => css`
    padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
    border-radius: ${theme.radius.normal};
  `);
const validateResourcePattern = (resource_pattern: string) => {
  if (!resource_pattern) {
    return 'Required';
  }
  return getResourcePatternError(resource_pattern) ?? undefined;
};

// Pattern input that lists the resources the pattern selects while it is
// focused, using the same matcher as the server, so an overly broad or empty
// selection is caught before saving.
const ResourcePatternInput = t0 => {
  const $ = _c(42);
  const {
    name,
    label,
    swr_key,
    fetcher,
    getName
  } = t0;
  const [t1, t2] = useField(name);
  const {
    value: pattern
  } = t1;
  const {
    error,
    touched
  } = t2;
  const [is_open, setIsOpen] = useState(false);
  const {
    data
  } = useSWR(swr_key, fetcher);
  let t3;
  if ($[0] !== pattern) {
    t3 = !!pattern && !getResourcePatternError(pattern);
    $[0] = pattern;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const is_valid_pattern = t3;
  let T0;
  let T1;
  let t10;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== data || $[3] !== getName || $[4] !== is_open || $[5] !== is_valid_pattern || $[6] !== label || $[7] !== name || $[8] !== pattern) {
    const saved_pattern = expandResourcePatternShorthand(pattern);
    const matched_names = data ? data.models.map(getName).filter(resource_name => matchesResourcePattern(resource_name, saved_pattern)) : [];
    const is_truncated = !!data?.pagination?.next;
    const scope = is_truncated ? `the first ${data.models.length} ${label}` : `${data?.models.length ?? 0} ${label}`;
    let t11;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setIsOpen(false);
        }
      };
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    const handleBlur = t11;
    let t12;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = e_0 => {
        if (e_0.key === "Escape") {
          setIsOpen(false);
        } else {
          if (e_0.key !== "Tab") {
            setIsOpen(true);
          }
        }
      };
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    const handleKeyDown = t12;
    T1 = Div;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = {
        t: 4
      };
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    T0 = Div;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = {
        position: "relative"
      };
      t5 = () => setIsOpen(true);
      $[21] = t4;
      $[22] = t5;
    } else {
      t4 = $[21];
      t5 = $[22];
    }
    t6 = handleBlur;
    t7 = handleKeyDown;
    if ($[23] !== name) {
      t8 = <TextInput m={0} name={name} placeholder="prod-* or !dev-*" format={field_formats.slugifyWithWildcard} validate={validateResourcePattern} required={true} />;
      $[23] = name;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t9 = is_open && is_valid_pattern && data && <StyledDropdown onMouseDown={_temp}><Text size="xs" semi={true} muted={true} m={{
        t: 1,
        x: 2
      }}>Sample of matching {label}</Text>{matched_names.length === 0 ? <Text size="s" warning={true} p={{
        x: 2,
        y: 1.5
      }} flex={{
        align: "center",
        gap: 1
      }}><Icon icon="warning" />{is_truncated ? `No match in ${scope}` : `No ${label} match this pattern`}</Text> : matched_names.map(_temp2)}</StyledDropdown>;
    $[2] = data;
    $[3] = getName;
    $[4] = is_open;
    $[5] = is_valid_pattern;
    $[6] = label;
    $[7] = name;
    $[8] = pattern;
    $[9] = T0;
    $[10] = T1;
    $[11] = t10;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    T0 = $[9];
    T1 = $[10];
    t10 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[25] !== T0 || $[26] !== t4 || $[27] !== t5 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t11 = <T0 style={t4} onFocus={t5} onBlur={t6} onKeyDown={t7}>{t8}{t9}</T0>;
    $[25] = T0;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== error || $[34] !== label || $[35] !== touched) {
    t12 = !(touched && error) && <Text m={{
      t: 1,
      b: 0
    }} size="xs" as="p" muted={true}><Icon icon="info" left={1} />Use * as a wildcard. Prefix with ! to match all {label} except that pattern.</Text>;
    $[33] = error;
    $[34] = label;
    $[35] = touched;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== T1 || $[38] !== t10 || $[39] !== t11 || $[40] !== t12) {
    t13 = <T1 m={t10}>{t11}{t12}</T1>;
    $[37] = T1;
    $[38] = t10;
    $[39] = t11;
    $[40] = t12;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  return t13;
};
export default ResourcePatternInput;
function _temp(e_1) {
  return e_1.preventDefault();
}
function _temp2(matched_name, index) {
  return <StyledMatchRow key={`${matched_name}-${index}`}><Text mono={true} size="s" dark={true} ellipsis={true}>{matched_name}</Text></StyledMatchRow>;
}